import React from 'react';
import { Box, Stack, Typography } from '@mui/material';
import UploadFileRounded from '@mui/icons-material/UploadFileRounded';
import HubRounded from '@mui/icons-material/HubRounded';
import { EmptyState, ListTable, Panel, StatusChip, type ListColumn, type StatusTone } from '../../../ui-kit';
import { fetchCqIntegrations, type CqIntegration } from '../../../services/carrierQuotingService';
import { fromNow, useLive } from '../rocco/live';
import { MailboxLogo } from '../policy-servicing/logos';
import { BrandLogo } from './logos';

function stateOf(row: CqIntegration): { tone: StatusTone; label: string } {
  if (!row.connected) return { tone: 'neutral', label: 'Not connected' };
  if (row.error) return { tone: 'warning', label: 'Needs attention' };
  return { tone: 'good', label: 'Connected' };
}

const ICONS: Record<string, React.ElementType> = { upload: UploadFileRounded };

/**
 * What Carrier Quoting is joined to: the documents people upload, the rater
 * the agency quotes through, the carrier websites the rater does not reach,
 * and the email proposals go out by.
 */
export default function IntegrationsPage() {
  const { data, error, loading } = useLive(fetchCqIntegrations, 15_000);
  if (error && !data) return <EmptyState message={error} />;

  const columns: ListColumn<CqIntegration>[] = [
    {
      key: 'name',
      header: 'System',
      grow: 1.2,
      minWidth: 210,
      render: (row) => {
        const Icon = ICONS[row.id] ?? HubRounded;
        return (
          <Stack direction="row" alignItems="center" spacing={1.25}>
            {row.logo ? <BrandLogo id={row.logo} name={row.name} size={28} /> : (
              <Box sx={(t) => ({
                width: 28, height: 28, flexShrink: 0, display: 'grid', placeItems: 'center',
                borderRadius: `${t.ds.radius.sm}px`, border: `1px solid ${t.tokens.border.subtle}`,
                color: t.tokens.brand.primary,
              })}>
                {row.id === 'email' ? <MailboxLogo provider="outlook" size={18} /> : <Icon sx={{ fontSize: 18 }} />}
              </Box>
            )}
            <Typography variant="body2" sx={(t) => ({ color: t.tokens.text.primary })}>{row.name}</Typography>
          </Stack>
        );
      },
    },
    {
      key: 'role',
      header: 'Does',
      width: 190,
      render: (row) => <Typography variant="body2" sx={(t) => ({ color: t.tokens.text.secondary })}>{row.role}</Typography>,
    },
    {
      key: 'state',
      header: 'State',
      width: 170,
      render: (row) => <StatusChip {...stateOf(row)} />,
    },
    {
      key: 'activity',
      header: 'Lately',
      grow: 2.2,
      minWidth: 260,
      render: (row) => (
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="body2" sx={(t) => ({ color: t.tokens.text.primary })}>
            {row.detail}{row.checked_at ? ` · last run ${fromNow(row.checked_at)}` : ''}
          </Typography>
          {row.error ? (
            <Typography variant="caption" sx={(t) => ({ color: t.tokens.semantic.danger })}>{row.error}</Typography>
          ) : null}
        </Box>
      ),
    },
  ];

  return (
    <Panel title="Connected systems" subtitle="Documents in, the rater the quotes go through, carrier websites, and proposals out">
      <ListTable
        columns={columns}
        rows={data?.integrations ?? []}
        getRowKey={(row) => row.id}
        loading={loading}
        emptyMessage="Nothing connected."
      />
    </Panel>
  );
}
